import { CSRF_TOKEN } from './csrf_token.js'

const ORGANIZER_ACCESS_TOKEN_KEY = 'organizer_access_token'
const ORGANIZER_REFRESH_TOKEN_KEY = 'organizer_refresh_token'
const TICKET_ACCESS_TOKEN_KEY = 'ticket_access_token'
const TICKET_REFRESH_TOKEN_KEY = 'ticket_refresh_token'
const REQUEST_TIMEOUT_MS = 15000

const isJwtToken = (token = '') => typeof token === 'string' && token.split('.').length === 3
const buildAuthorizationHeader = (token = '') => token ? { Authorization: `${isJwtToken(token) ? 'Bearer' : 'Token'} ${token}` } : {}
const getTokenKeys = (authKind = 'organizer') => authKind === 'ticket' ? { access: TICKET_ACCESS_TOKEN_KEY, refresh: TICKET_REFRESH_TOKEN_KEY } : { access: ORGANIZER_ACCESS_TOKEN_KEY, refresh: ORGANIZER_REFRESH_TOKEN_KEY }
const getStoredAccessToken = (authKind = 'organizer') => window.localStorage.getItem(getTokenKeys(authKind).access) || ''
const clearStoredTokens = (authKind = 'organizer') => { const keys = getTokenKeys(authKind); window.localStorage.removeItem(keys.access); window.localStorage.removeItem(keys.refresh) }
const inferAuthKind = (token = '') => { const organizerToken = window.localStorage.getItem(ORGANIZER_ACCESS_TOKEN_KEY) || ''; const ticketToken = window.localStorage.getItem(TICKET_ACCESS_TOKEN_KEY) || ''; if (token && ticketToken && token === ticketToken) return 'ticket'; if (token && organizerToken && token === organizerToken) return 'organizer'; if (!token && organizerToken) return 'organizer'; if (!token && ticketToken) return 'ticket'; return 'organizer' }

async function parseResponse(response) {
  if (response.status === 204) return ''
  if (response.status === 404) return null
  const contentType = response.headers.get('content-type') || ''
  const payload = contentType.includes('application/json') ? await response.json() : await response.text()
  if (response.ok) return payload
  if (payload && typeof payload === 'object') return payload
  if (response.status >= 500) return { err: `El servidor no respondio a tiempo (${response.status}).` }
  return { err: `La solicitud fallo (${response.status}).` }
}

function fetchWithTimeout(endpoint, config = {}, timeoutMs = REQUEST_TIMEOUT_MS) {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)

  return fetch(endpoint, { ...config, signal: controller.signal })
    .finally(() => {
      window.clearTimeout(timer)
    })
}

const buildJsonConfig = (method, data, headers = {}) => ({ method: method || 'GET', credentials: 'same-origin', body: data !== undefined ? JSON.stringify(data) : null, headers: { 'content-type': 'application/json', 'X-CSRFTOKEN': CSRF_TOKEN, ...headers } })
const buildFormConfig = (method, data, headers = {}) => ({ method: method || 'GET', credentials: 'same-origin', body: data, headers: { 'X-CSRFTOKEN': CSRF_TOKEN, ...headers } })

async function refreshJwtToken(authKind = 'organizer') {
  const keys = getTokenKeys(authKind)
  const refreshToken = window.localStorage.getItem(keys.refresh) || ''
  if (!isJwtToken(refreshToken)) { clearStoredTokens(authKind); return '' }
  const response = await fetchWithTimeout('/api/auth/token/refresh/', buildJsonConfig('POST', { refresh: refreshToken }), 10000)
  const payload = await parseResponse(response)
  if (!response.ok || !payload || !payload.access) { clearStoredTokens(authKind); return '' }
  window.localStorage.setItem(keys.access, payload.access)
  if (payload.refresh) window.localStorage.setItem(keys.refresh, payload.refresh)
  return payload.access
}

async function requestWithAuth(endpoint, config, options = {}) {
  const authKind = options.authKind || 'organizer'
  let activeToken = options.token || getStoredAccessToken(authKind)
  let requestConfig = { ...config, headers: { ...(config.headers || {}), ...buildAuthorizationHeader(activeToken) } }
  let response = await fetchWithTimeout(endpoint, requestConfig)
  if ((response.status === 401 || response.status === 403) && isJwtToken(activeToken)) {
    const refreshedAccess = await refreshJwtToken(authKind)
    if (refreshedAccess) {
      activeToken = refreshedAccess
      requestConfig = { ...config, headers: { ...(config.headers || {}), ...buildAuthorizationHeader(activeToken) } }
      response = await fetchWithTimeout(endpoint, requestConfig)
    }
  }
  return parseResponse(response)
}

function apiService(endpoint, method, data) {
  return requestWithAuth(endpoint, buildJsonConfig(method, data), { authKind: 'organizer' }).catch((error) => error && error.name === 'AbortError' ? ({ err: 'La solicitud tardo demasiado. Intenta de nuevo.' }) : ({ err: 'No pudimos conectar con el servidor.' }))
}

function apiServiceAuth(endpoint, method, data, token, authKind) {
  return requestWithAuth(endpoint, buildJsonConfig(method, data), { token: token || '', authKind: authKind || inferAuthKind(token || '') }).catch((error) => error && error.name === 'AbortError' ? ({ err: 'La solicitud tardo demasiado. Intenta de nuevo.' }) : ({ err: 'No pudimos conectar con el servidor.' }))
}

function apiServiceSession(endpoint, method, data) {
  return fetchWithTimeout(endpoint, buildJsonConfig(method, data)).then(parseResponse).catch((error) => error && error.name === 'AbortError' ? ({ err: 'La solicitud tardo demasiado. Intenta de nuevo.' }) : ({ err: 'No pudimos conectar con el servidor.' }))
}

function apiServiceForm(endpoint, method, data) {
  return requestWithAuth(endpoint, buildFormConfig(method, data), { authKind: 'organizer' }).catch((error) => error && error.name === 'AbortError' ? ({ err: 'La solicitud tardo demasiado. Intenta de nuevo.' }) : ({ err: 'No pudimos conectar con el servidor.' }))
}

export { apiService, apiServiceAuth, apiServiceForm, apiServiceSession }
